import WebsiteRepositoryInterface, { WebsiteComposite } from '@/services/Website/interfaces';
import { ServerRepositoryInterface } from '@/services/Server/Interfaces';
import {
  CONSOLE_ACTION_SUBCOMMANDS,
  CONSOLE_COMMANDS,
  CONSOLE_FILTER_KEYS,
  CONSOLE_LOG_TYPES,
  CONSOLE_OPEN_TARGETS,
  CONSOLE_STATUS_VALUES,
  getConsoleCommand,
} from './commands';
import {
  ConsoleActionAvailability,
  listAvailableActions,
} from './actions';
import {
  loadCommandHistory,
  loadRecentOwners,
  loadRecentServers,
  loadRecentTargets,
} from './history';
import { tokenize } from './parser';
import {
  ConsoleSiteContext,
  ConsoleSuggestion,
} from './types';

export interface BuildConsoleSuggestionsOptions {
  context?: ConsoleSiteContext | null;
  scopes?: string[];
  userScopes?: string[];
  composite?: WebsiteComposite | null;
  availableActions?: ConsoleActionAvailability[];
}

function endsWithWhitespace(value: string): boolean {
  return /\s$/.test(value);
}

function replaceLastToken(raw: string, insertText: string): string {
  if (!raw.trim() || endsWithWhitespace(raw)) {
    return `${raw.replace(/\s+$/, ' ')}${insertText}`;
  }

  const match = raw.match(/^(.*?)(\S+)$/);
  if (!match) {
    return insertText;
  }
  return `${match[1]}${insertText}`;
}

function suggestionId(group: string, label: string): string {
  return `${group}:${label}`;
}

function rankDomainSuggestions(
  sites: Array<{ id: number; domain: string; status: string; originServer?: string }>,
  query: string,
): Array<{ id: number; domain: string; status: string; originServer?: string }> {
  const q = query.toLowerCase();
  return sites.slice().sort((a, b) => {
    const aExact = a.domain.toLowerCase() === q ? 0 : 1;
    const bExact = b.domain.toLowerCase() === q ? 0 : 1;
    if (aExact !== bExact) {
      return aExact - bExact;
    }
    const aStarts = a.domain.toLowerCase().indexOf(q) === 0 ? 0 : 1;
    const bStarts = b.domain.toLowerCase().indexOf(q) === 0 ? 0 : 1;
    if (aStarts !== bStarts) {
      return aStarts - bStarts;
    }
    return a.domain.localeCompare(b.domain);
  });
}

function websiteSuggestionDescription(site: {
  id: number;
  status: string;
  originServer?: string;
}): string {
  const server = site.originServer ? site.originServer : '—';
  return `#${site.id} · ${site.status} · ${server}`;
}

function commandSuggestion(cmd: typeof CONSOLE_COMMANDS[0], spaceSelect: boolean): ConsoleSuggestion {
  const needsSpace = !!(cmd.requiresOperand || cmd.optionalOperand || CONSOLE_ACTION_SUBCOMMANDS[cmd.name]);
  return {
    id: suggestionId('command', cmd.name),
    label: `/${cmd.name}`,
    insertText: cmd.aliasOf || `/${cmd.name}${needsSpace ? ' ' : ''}`,
    description: cmd.description,
    group: cmd.aliasOf ? 'Aliases' : 'Commands',
    spaceSelect,
  };
}

function resolveAvailableActions(
  options?: BuildConsoleSuggestionsOptions,
): ConsoleActionAvailability[] {
  if (options && options.availableActions) {
    return options.availableActions;
  }
  const scopes = (options && (options.scopes || options.userScopes)) || [];
  const composite = options && options.composite !== undefined ? options.composite : null;
  return listAvailableActions(composite, scopes);
}

async function suggestDomains(
  repository: WebsiteRepositoryInterface,
  query: string,
  commandName: string,
  prefix?: string,
): Promise<ConsoleSuggestion[]> {
  const suggestions: ConsoleSuggestion[] = [];
  const insertPrefix = prefix !== undefined ? prefix : `/${commandName}`;

  loadRecentTargets()
    .filter((target) => !query || target.toLowerCase().indexOf(query.toLowerCase()) !== -1)
    .slice(0, 5)
    .forEach((target) => {
      suggestions.push({
        id: suggestionId('recent', target),
        label: target,
        insertText: `${insertPrefix} ${target}`,
        description: 'Recent target',
        group: 'Recent',
        spaceSelect: false,
      });
    });

  if (query.length >= 2 && query.indexOf(':') === -1
    && (CONSOLE_LOG_TYPES as readonly string[]).indexOf(query.toLowerCase()) === -1) {
    try {
      const response = await repository.searchWebsites(1, query, undefined, undefined, undefined, 8);
      rankDomainSuggestions(response.Websites || [], query).forEach((site) => {
        suggestions.push({
          id: suggestionId('site', String(site.id)),
          label: site.domain,
          insertText: `${insertPrefix} ${site.id}`,
          description: websiteSuggestionDescription(site),
          group: 'Websites',
          spaceSelect: false,
        });
      });
    } catch (_e) {
      // Ignore.
    }
  }

  return suggestions;
}

function suggestSubcommands(
  input: string,
  commandName: string,
  subcommands: string[],
  currentArg: string,
  completingToken: boolean,
): ConsoleSuggestion[] {
  const suggestions: ConsoleSuggestion[] = [];
  const filter = completingToken && currentArg ? currentArg.toLowerCase() : '';

  subcommands
    .filter((sub) => !filter || sub.indexOf(filter) === 0)
    .forEach((sub) => {
      suggestions.push({
        id: suggestionId('subcommand', `${commandName}.${sub}`),
        label: sub,
        insertText: endsWithWhitespace(input) || !completingToken
          ? `/${commandName} ${sub} `
          : replaceLastToken(input, `${sub} `),
        description: `/${commandName} ${sub}`,
        group: 'Subcommands',
        spaceSelect: false,
      });
    });

  return suggestions;
}

function suggestAvailableActions(
  available: ConsoleActionAvailability[],
  filter: string,
): ConsoleSuggestion[] {
  const q = filter.toLowerCase();
  return available
    .filter((item) => {
      if (!q) {
        return true;
      }
      const cmd = item.action.command.toLowerCase();
      const label = item.action.label.toLowerCase();
      return cmd.indexOf(q) !== -1 || label.indexOf(q) !== -1
        || cmd.replace(/^\//, '').indexOf(q.replace(/^\//, '')) === 0;
    })
    .map((item) => ({
      id: suggestionId('action', item.action.id),
      label: item.action.command,
      insertText: `${item.action.command} `,
      description: item.available
        ? item.action.description
        : (item.disabledReason || item.action.description),
      group: item.action.group,
      disabled: !item.available,
      disabledReason: item.disabledReason,
      spaceSelect: false,
    }));
}

let serverCache: string[] | null = null;
let serverCachePromise: Promise<string[]> | null = null;

async function loadServerHostnames(serverRepository?: ServerRepositoryInterface): Promise<string[]> {
  if (serverCache) {
    return serverCache;
  }
  if (!serverRepository) {
    return loadRecentServers();
  }
  if (!serverCachePromise) {
    serverCachePromise = serverRepository.getAllServers()
      .then((servers) => {
        serverCache = (servers || []).map((s) => s.hostname).filter(Boolean);
        return serverCache;
      })
      .catch(() => {
        serverCachePromise = null;
        return loadRecentServers();
      });
  }
  return serverCachePromise;
}

export async function buildConsoleSuggestions(
  rawInput: string,
  repository: WebsiteRepositoryInterface,
  requestId: number,
  serverRepository?: ServerRepositoryInterface,
  options?: BuildConsoleSuggestionsOptions,
): Promise<{ requestId: number; suggestions: ConsoleSuggestion[] }> {
  const input = rawInput;
  const trimmed = input.trim();
  const availableActions = resolveAvailableActions(options);

  // Slash-command catalog only after the user starts a `/` command.
  if (!trimmed) {
    return { requestId, suggestions: [] };
  }

  if (!trimmed.startsWith('/')) {
    const suggestions: ConsoleSuggestion[] = [];

    loadRecentTargets()
      .filter((target) => target.toLowerCase().indexOf(trimmed.toLowerCase()) !== -1)
      .slice(0, 5)
      .forEach((target) => {
        suggestions.push({
          id: suggestionId('recent', target),
          label: target,
          insertText: `/find ${target}`,
          description: 'Recent target',
          group: 'Recent',
          spaceSelect: false,
        });
      });

    if (trimmed.length >= 2) {
      try {
        const response = await repository.searchWebsites(1, trimmed, undefined, undefined, undefined, 8);
        rankDomainSuggestions(response.Websites || [], trimmed).forEach((site) => {
          suggestions.push({
            id: suggestionId('site', String(site.id)),
            label: site.domain,
            insertText: `/site ${site.id}`,
            description: websiteSuggestionDescription(site),
            group: 'Websites',
            spaceSelect: false,
          });
        });
      } catch (_e) {
        // Ignore search failures for autocomplete.
      }
    }

    return { requestId, suggestions };
  }

  if (trimmed === '/') {
    const actionSuggestions = suggestAvailableActions(availableActions, '');
    const commandSuggestions = CONSOLE_COMMANDS.map((cmd) => commandSuggestion(cmd, true));
    return {
      requestId,
      suggestions: actionSuggestions.concat(commandSuggestions),
    };
  }

  const tokens = tokenize(trimmed);
  const commandToken = tokens[0] || '';
  const commandName = commandToken.slice(1).toLowerCase();
  const definition = getConsoleCommand(commandName);
  const completingToken = !endsWithWhitespace(input);
  const args = tokens.slice(1);
  const currentArg = completingToken && args.length ? args[args.length - 1] : '';

  // Still typing the first command token (e.g. `/dis`).
  if (tokens.length === 1 && completingToken && !definition) {
    const actionFilter = commandToken;
    const actionSuggestions = suggestAvailableActions(availableActions, actionFilter);
    const commandSuggestions = CONSOLE_COMMANDS
      .filter((cmd) => cmd.name.indexOf(commandName) === 0)
      .map((cmd) => commandSuggestion(cmd, true));
    return {
      requestId,
      suggestions: actionSuggestions.concat(commandSuggestions),
    };
  }

  if (tokens.length === 1 && completingToken && definition) {
    // Exact prefix match while editing first token — still a command-name completion.
    const actionSuggestions = suggestAvailableActions(availableActions, commandToken);
    return {
      requestId,
      suggestions: actionSuggestions.concat([commandSuggestion(definition, true)]),
    };
  }

  if (!definition) {
    return { requestId, suggestions: [] };
  }

  const suggestions: ConsoleSuggestion[] = [];
  const subcommands = CONSOLE_ACTION_SUBCOMMANDS[definition.name];

  if (definition.name === 'sites') {
    if (!currentArg || currentArg.indexOf(':') === -1) {
      CONSOLE_FILTER_KEYS.forEach((key) => {
        if (!currentArg || key.indexOf(currentArg.toLowerCase()) === 0) {
          const insertText = args.length === 0 && !endsWithWhitespace(input)
            ? `/${definition.name} ${key}:`
            : replaceLastToken(input, `${key}:`);
          suggestions.push({
            id: suggestionId('filter', key),
            label: `${key}:`,
            insertText,
            description: `Filter by ${key}`,
            group: 'Filters',
            spaceSelect: false,
          });
        }
      });
    } else {
      const key = currentArg.slice(0, currentArg.indexOf(':')).toLowerCase();
      const value = currentArg.slice(currentArg.indexOf(':') + 1);

      if (key === 'status') {
        CONSOLE_STATUS_VALUES
          .filter((status) => !value || status.indexOf(value.toUpperCase()) === 0)
          .forEach((status) => {
            suggestions.push({
              id: suggestionId('status', status),
              label: `status:${status}`,
              insertText: replaceLastToken(input, `status:${status} `),
              description: 'Website status',
              group: 'Status',
              spaceSelect: false,
            });
          });
      } else if (key === 'domain' && value.length >= 2) {
        try {
          const response = await repository.searchWebsites(1, value, undefined, undefined, undefined, 8);
          rankDomainSuggestions(response.Websites || [], value).forEach((site) => {
            suggestions.push({
              id: suggestionId('site', String(site.id)),
              label: site.domain,
              insertText: replaceLastToken(input, `domain:${site.domain} `),
              description: websiteSuggestionDescription(site),
              group: 'Websites',
              spaceSelect: false,
            });
          });
        } catch (_e) {
          // Ignore.
        }
      } else if (key === 'server') {
        const recent = loadRecentServers()
          .filter((s) => !value || s.toLowerCase().indexOf(value.toLowerCase()) === 0);
        recent.forEach((server) => {
          suggestions.push({
            id: suggestionId('recent-server', server),
            label: `server:${server}`,
            insertText: replaceLastToken(input, `server:${server} `),
            description: 'Recent server',
            group: 'Recent',
            spaceSelect: false,
          });
        });
        if (value.length >= 2) {
          const hosts = await loadServerHostnames(serverRepository);
          hosts
            .filter((h) => h.toLowerCase().indexOf(value.toLowerCase()) !== -1)
            .slice(0, 8)
            .forEach((server) => {
              if (recent.indexOf(server) !== -1) {
                return;
              }
              suggestions.push({
                id: suggestionId('server', server),
                label: `server:${server}`,
                insertText: replaceLastToken(input, `server:${server} `),
                description: 'Server',
                group: 'Servers',
                spaceSelect: false,
              });
            });
        }
      } else if (key === 'owner') {
        loadRecentOwners()
          .filter((o) => !value || o.toLowerCase().indexOf(value.toLowerCase()) === 0)
          .slice(0, 8)
          .forEach((owner) => {
            suggestions.push({
              id: suggestionId('owner', owner),
              label: `owner:${owner}`,
              insertText: replaceLastToken(input, `owner:${owner} `),
              description: 'Recent owner',
              group: 'Owners',
              spaceSelect: false,
            });
          });
      }
    }

    return { requestId, suggestions };
  }

  if (definition.name === 'open') {
    const firstArg = args[0] || '';
    const firstLower = firstArg.toLowerCase();
    const suggestingTarget = args.length === 0
      || (args.length === 1 && completingToken && !endsWithWhitespace(input));

    if (suggestingTarget) {
      CONSOLE_OPEN_TARGETS
        .filter((target) => !firstArg || target.indexOf(firstLower) === 0)
        .forEach((target) => {
          suggestions.push({
            id: suggestionId('open-target', target),
            label: target,
            insertText: `/open ${target} `,
            description: `Navigate to ${target}`,
            group: 'Targets',
            spaceSelect: false,
          });
        });
    }

    const openTarget = (CONSOLE_OPEN_TARGETS as readonly string[]).indexOf(firstLower) !== -1
      ? firstLower
      : undefined;
    const domainQuery = openTarget
      ? (args.length >= 2 ? (completingToken ? currentArg : '') : (endsWithWhitespace(input) ? '' : ''))
      : (completingToken ? currentArg : '');

    if (!openTarget || args.length >= 2 || endsWithWhitespace(input)) {
      const prefix = openTarget ? `/open ${openTarget}` : '/open';
      const domainSuggestions = await suggestDomains(
        repository,
        domainQuery,
        'open',
        prefix,
      );
      domainSuggestions.forEach((item) => suggestions.push(item));
    }

    if (!suggestions.length) {
      definition.examples.forEach((example, index) => {
        suggestions.push({
          id: suggestionId('example', String(index)),
          label: example,
          insertText: example,
          description: 'Example',
          group: 'Examples',
          spaceSelect: false,
        });
      });
    }

    return { requestId, suggestions };
  }

  // Action commands with required subcommands (staging, alias, wp, db manage, …)
  if (subcommands && subcommands.length) {
    const hasSubcommand = args.length > 0
      && subcommands.indexOf(args[0].toLowerCase()) !== -1;
    const suggestingSub = args.length === 0
      || (args.length === 1 && completingToken && !hasSubcommand);

    if (suggestingSub) {
      suggestSubcommands(input, definition.name, subcommands, currentArg, completingToken)
        .forEach((item) => suggestions.push(item));
      return { requestId, suggestions };
    }

    const sub = args[0].toLowerCase();
    const domainQuery = args.length >= 2
      ? (completingToken ? currentArg : '')
      : '';
    const prefix = `/${definition.name} ${sub}`;
    const domainSuggestions = await suggestDomains(repository, domainQuery, definition.name, prefix);
    domainSuggestions.forEach((item) => suggestions.push(item));

    if (!domainQuery && !domainSuggestions.length) {
      definition.examples.forEach((example, index) => {
        suggestions.push({
          id: suggestionId('example', String(index)),
          label: example,
          insertText: example,
          description: 'Example',
          group: 'Examples',
          spaceSelect: false,
        });
      });
    }

    return { requestId, suggestions };
  }

  if (definition.name === 'logs') {
    if (!currentArg) {
      CONSOLE_LOG_TYPES.forEach((type) => {
        suggestions.push({
          id: suggestionId('logtype', type),
          label: type,
          insertText: endsWithWhitespace(input) || args.length
            ? replaceLastToken(input, `${type} `)
            : `/logs ${type} `,
          description: 'Log type',
          group: 'Log type',
          spaceSelect: false,
        });
      });
    }
  }

  if (definition.name === 'retry') {
    // Suggest domains after optional numeric ids, or when no args yet.
    const numericArgs = args.filter((arg) => /^\d+$/.test(arg));
    const maybeDomain = args.length && !/^\d+$/.test(args[args.length - 1])
      ? (completingToken ? currentArg : '')
      : (endsWithWhitespace(input) || !args.length ? '' : '');
    if (numericArgs.length < 2 || args.length >= 2) {
      const domainSuggestions = await suggestDomains(
        repository,
        maybeDomain || (completingToken && args.length === 1 && !/^\d+$/.test(args[0]) ? currentArg : ''),
        'retry',
        numericArgs.length >= 2 ? `/retry ${numericArgs[0]} ${numericArgs[1]}` : '/retry',
      );
      domainSuggestions.forEach((item) => suggestions.push(item));
    }
    if (!suggestions.length) {
      definition.examples.forEach((example, index) => {
        suggestions.push({
          id: suggestionId('example', String(index)),
          label: example,
          insertText: example,
          description: 'Example',
          group: 'Examples',
          spaceSelect: false,
        });
      });
    }
    return { requestId, suggestions };
  }

  if (definition.requiresOperand || definition.optionalOperand) {
    const query = currentArg || '';

    const domainSuggestions = await suggestDomains(repository, query, definition.name);
    domainSuggestions.forEach((item) => suggestions.push(item));

    if (!query && !domainSuggestions.length) {
      definition.examples.forEach((example, index) => {
        suggestions.push({
          id: suggestionId('example', String(index)),
          label: example,
          insertText: example,
          description: 'Example',
          group: 'Examples',
          spaceSelect: false,
        });
      });
    }

    return { requestId, suggestions };
  }

  if (!args.length) {
    loadCommandHistory().slice(0, 6).forEach((entry, index) => {
      suggestions.push({
        id: suggestionId('history', String(index)),
        label: entry,
        insertText: entry,
        description: 'History',
        group: 'History',
        spaceSelect: false,
      });
    });
  }

  return { requestId, suggestions };
}

export function applySuggestion(_rawInput: string, suggestion: ConsoleSuggestion): string {
  return suggestion.insertText;
}

/**
 * Space selects a suggestion when it opts in via spaceSelect, or when the
 * input is still editing the first command token (e.g. `/dis`).
 */
export function shouldSpaceSelectSuggestion(
  rawInput: string,
  suggestion: { spaceSelect?: boolean },
): boolean {
  if (suggestion.spaceSelect === true) {
    return true;
  }
  return /^\/\S*$/.test(rawInput);
}
